import { AdvancedOptions, type FilterValues } from "./search/AdvancedOptions";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { apiService, type Teacher } from "../services/api.service";
import { useHeaderActions } from "../context/HeaderActionsContext";
import { Card, CardContent } from "./ui/card";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "./ui/table";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Avatar, AvatarFallback } from "./ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Skeleton } from "./ui/skeleton";
import {
  Pencil,
  AlertCircle,
  Loader2,
  Users,
  RefreshCw,
  Search,
} from "lucide-react";

export const TeacherUsage = () => {
  const [filters, setFilters] = useState<FilterValues>({});
  const [organizations, setOrganizations] = useState<{ id: string; name: string }[]>([]);
  useEffect(() => { apiService.getOrganizations().then(setOrganizations).catch(() => {}); }, []);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingTeacher, setEditingTeacher] = useState<Teacher | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const teacherGeneration = useRef(0);
  const { setActions } = useHeaderActions();

  const filteredTeachers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return teachers.filter((teacher) =>
      (!filters.organization_id || teacher.organization_id === filters.organization_id) &&
      (!query || teacher.name?.toLowerCase().includes(query) || teacher.email?.toLowerCase().includes(query) || String(teacher.teacher_id).includes(query)),
    );
  }, [teachers, searchQuery, filters]);

  const loadTeachers = useCallback(async () => {
    const generation = ++teacherGeneration.current;
    try {
      setIsLoading(true);
      setError(null);
      setTeachers([]);
      const data = await apiService.getTeachers(filters.organization_id || undefined);
      if (generation === teacherGeneration.current) setTeachers(data);
    } catch (err) {
      if (generation === teacherGeneration.current) setError("Failed to load teachers");
    } finally {
      if (generation === teacherGeneration.current) setIsLoading(false);
    }
  }, [filters.organization_id]);

  useEffect(() => {
    loadTeachers();
    return () => { teacherGeneration.current += 1; };
  }, [loadTeachers]);

  // Set header actions
  useEffect(() => {
    setActions(
      <Button
        variant="outline"
        size="icon"
        onClick={loadTeachers}
        disabled={isLoading}
        className="h-8 w-8"
      >
        <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
      </Button>,
    );
    return () => setActions(null);
  }, [setActions, isLoading, loadTeachers]);

  useEffect(() => {
    const handleSlash = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.isContentEditable
      )
        return;
      if (event.key === "/") {
        event.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handleSlash);
    return () => window.removeEventListener("keydown", handleSlash);
  }, []);

  const msToHours = (ms: number) => (ms / (1000 * 60 * 60)).toFixed(1);
  const hoursToMs = (hours: number) => Math.floor(hours * 1000 * 60 * 60);

  const openEditDialog = (teacher: Teacher) => {
    setEditingTeacher(teacher);
    setEditValue(Number(msToHours(teacher.max_usage_allowed_ms)));
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingTeacher(null);
    setEditValue(0);
  };

  const saveUsageLimit = async () => {
    if (!editingTeacher) return;

    try {
      setIsSaving(true);
      const limitMs = hoursToMs(editValue);
      await apiService.updateTeacherUsageLimit(
        editingTeacher.teacher_id,
        limitMs,
      );

      setTeachers(
        teachers.map((t) =>
          t.teacher_id === editingTeacher.teacher_id
            ? { ...t, max_usage_allowed_ms: limitMs }
            : t,
        ),
      );

      closeDialog();
    } catch (err) {
      setError("Failed to update usage limit");
    } finally {
      setIsSaving(false);
    }
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return "T";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return ((parts[0]?.[0] ?? "") + (parts.at(-1)?.[0] ?? "")).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="h-full overflow-auto p-4">
        <div className="mx-auto w-full max-w-6xl">
          <Card className="border border-border/60 shadow-sm">
            <CardContent className="p-4 space-y-4">
              <Skeleton className="h-9 w-full" />
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="size-7 rounded-full" />
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-4 w-44 ml-auto" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                  <Skeleton className="size-7" />
              </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <AlertCircle className="size-6 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground mb-4">{error}</p>
        <Button onClick={loadTeachers} variant="outline" size="sm">
          Try Again
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="h-full overflow-auto p-4">
        <div className="mx-auto w-full max-w-6xl">
          <Card className="border border-border/60 shadow-sm">
            <CardContent className="flex min-h-0 flex-1 flex-col p-4">
              <div className="mb-4 flex flex-wrap items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    ref={searchInputRef}
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 pr-14"
                  />
                  <kbd className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 rounded border bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                    /
                  </kbd>
                </div>
                  <AdvancedOptions values={filters} onChange={setFilters} fields={[
                  { key: "organization_id", label: "Organization", options: organizations.map((org) => ({ value: org.id, label: org.name })) },
                ]} />
              </div>
              {filteredTeachers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16">
                  <Users className="size-8 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    {searchQuery
                      ? "No matching teachers found"
                      : "No teachers found"}
                  </p>
                </div>
              ) : (
                <div className="flex-1 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-b">
                        <TableHead className="text-xs uppercase tracking-wide text-muted-foreground font-medium px-3 h-9">
                          Teacher
                        </TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-muted-foreground font-medium px-3 h-9">
                          Email
                        </TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-muted-foreground font-medium px-3 h-9">
                          Usage Limit
                        </TableHead>
                        <TableHead className="text-xs uppercase tracking-wide text-muted-foreground font-medium px-3 h-9 text-right">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredTeachers.map((teacher) => (
                        <TableRow
                          key={teacher.teacher_id}
                          className="hover:bg-muted/50 border-b last:border-0"
                        >
                          <TableCell className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <Avatar className="size-7">
                                <AvatarFallback className="bg-muted text-muted-foreground text-xs font-medium">
                                  {getInitials(teacher.name)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-medium text-sm">
                                {teacher.name || "Unknown"}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="px-3 py-3 text-sm text-muted-foreground">
                            {teacher.email || "N/A"}
                          </TableCell>
                          <TableCell className="px-3 py-3">
                            <div className="flex items-center gap-2">
                              <Badge
                                variant="secondary"
                                className="bg-muted text-foreground font-medium text-xs"
                              >
                                {msToHours(teacher.max_usage_allowed_ms)} hrs
                              </Badge>
                              <div className="hidden sm:block h-1.5 w-16 rounded-full bg-muted overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-lime-600/60 transition-all duration-300"
                                  style={{
                                    width: `${Math.min(100, (teacher.max_usage_allowed_ms / (100 * 60 * 60 * 1000)) * 100)}%`,
                                  }}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="px-3 py-3 text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(teacher)}
                              className="text-muted-foreground hover:text-foreground"
                            >
                              <Pencil className="size-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="sm:max-w-sm"
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              saveUsageLimit();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>Edit Usage Limit</DialogTitle>
            <DialogDescription>
              {editingTeacher?.name || "Teacher"} — Cmd/Ctrl+Enter to save
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="usageLimit" className="text-sm">
                Hours
              </Label>
              <Input
                id="usageLimit"
                type="number"
                value={editValue}
                onChange={(e) => setEditValue(Number(e.target.value))}
                step="0.5"
                min="0"
                placeholder="0"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
              Cancel
            </Button>
            <Button onClick={saveUsageLimit} disabled={isSaving}>
              {isSaving ? <Loader2 className="size-4 animate-spin" /> : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
